import { useListScroll } from '@/hooks/use-list-scroll'
import { TaskManagementLink } from '@/components/task-management-link'
import { OrderReturnLink } from '@/components/finance-navigation'
import React, { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { useNotificationContext } from '@/contexts/notification-context'
import { NotificationMarquee } from '@/components/notification-marquee'
import { ErrorDisplay } from '@/components/error-display'
import { useCurrentUser } from '@/hooks/use-auth'
import { useSidebarStore } from '@/stores/sidebar'
import { useTabbarStore } from '@/stores/tabbar'
import { isTabAccessibleForRole } from '@/lib/menu/utils'
import { menuConfig } from '@/lib/menu/config'
import type { UserRole } from '@/types/menu'
// 使用 Ant Design 组件
import { SideNav, ChromeTabs } from '@/components/ant'
import { Toolbar } from '@/components/toolbar'
import { ImpersonationBanner } from '@/components/impersonation-banner'
import { OrderDetailModal } from '@/components/order-detail-modal'

interface DashboardLayoutProps {
  children: React.ReactNode
}

/**
 * 内容区骨架屏组件
 * 在页面切换时显示，保持框架UI可见
 */
function ContentSkeleton() {
  return (
    <div className="p-2 space-y-2 animate-pulse">
      {/* 统计卡片骨架 */}
      <div className="grid grid-cols-4 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 bg-slate-100 rounded-lg" />
        ))}
      </div>

      {/* 表格骨架 */}
      <div className="space-y-2">
        <div className="h-10 bg-slate-100 rounded" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 bg-slate-50 rounded" />
        ))}
      </div>
    </div>
  )
}

/**
 * 工具栏骨架屏（用户信息加载中）
 */
function ToolbarSkeleton() {
  return (
    <header className="flex h-12 shrink-0 items-center border-b border-slate-200/60 bg-white/90 backdrop-blur-sm px-6 shadow-sm">
      <div className="flex items-center flex-1">
        <div className="h-5 w-32 bg-slate-200 rounded animate-pulse" />
      </div>
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 bg-slate-200 rounded-lg animate-pulse" />
        <div className="h-8 w-8 bg-slate-200 rounded-lg animate-pulse" />
        <div className="w-px h-6 bg-slate-200" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200 animate-pulse" />
          <div className="h-4 w-16 bg-slate-200 rounded animate-pulse" />
        </div>
      </div>
    </header>
  )
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { unreadCount, isConnected } = useNotificationContext()

  // Zustand 状态
  const collapsed = useSidebarStore((state) => state.collapsed)

  // 获取当前用户信息
  const { data: currentUser, isLoading: userLoading, error: userError } = useCurrentUser()

  const contentRef = useListScroll(currentUser?.id)

  // 纵深防御：按当前用户权限过滤菜单标签。
  // 即使有跨账号残留/越权直达，非管理员也不会保留无权访问的标签（管理员可见全部）。
  const filterTabsByRole = useTabbarStore((s) => s.filterTabs)
  useEffect(() => {
    const roles = (currentUser?.roles as UserRole[] | undefined) || []
    if (roles.length === 0 || roles.includes('ADMIN' as UserRole)) return
    filterTabsByRole((tab) =>
      roles.some((r) => isTabAccessibleForRole((tab.href || tab.id) as string, r, menuConfig))
    )
  }, [currentUser, filterTabsByRole])

  // 错误状态优先处理（用户被删除、token无效等）
  // 只有在有实际错误且不是加载中时才显示错误
  if (userError && !userLoading) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <ErrorDisplay error={userError as Error} />
      </div>
    )
  }

  return (
    <div className="h-screen bg-slate-50 overflow-hidden">
      {/* 侧边栏 - 始终可见 */}
      <SideNav />

      {/* 主内容区 */}
      <div
        className={cn(
          'h-screen flex flex-col transition-all duration-300',
          collapsed ? 'ml-14' : 'ml-48'
        )}
      >
        {/* 化身模式提示条 - 进入化身模式时显示在最顶部 */}
        <div className="shrink-0">
          <ImpersonationBanner />
        </div>

        {/* 标签栏 - 始终可见 */}
        <div className="shrink-0">
          <ChromeTabs />
        </div>

        {/* 顶部工具栏 - 始终可见，加载时显示骨架 */}
        <div className="shrink-0">
          {userLoading || !currentUser ? (
            <ToolbarSkeleton />
          ) : (
            <Toolbar currentUser={currentUser} unreadCount={unreadCount} isConnected={isConnected} />
          )}
        </div>

        {/* 滚动通知栏 */}
        <NotificationMarquee />

        {/* 页面内容 - 可滚动区域 */}
        <main ref={contentRef} className="flex-1 overflow-y-auto min-h-0">
          {userLoading || !currentUser ? (
            <ContentSkeleton />
          ) : (
            <div className="p-2">
              <OrderReturnLink />
              <TaskManagementLink />
              {children}
            </div>
          )}
        </main>
      </div>

      {/* 全局订单详情弹窗:任务处理时查看订单以模态盖住当前页,不跳转 */}
      <OrderDetailModal />
    </div>
  )
}
